import { autoinject, BindingEngine, computedFrom, transient, useView } from "aurelia-framework";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { AttachmentClientService } from "../../../../../../services/attachment/attachments-client.service";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { PscInspectionStatusEnum } from "../../../../../../models/vetting/vetting.models";
import { PscClientModel } from "../../../../../../models/pscs/psc.models";
import { RightshipEntityAttachmentManager } from "../../../common-steps/rightship-entity-attachment/rightship-entity-attachment-manager";
import { BindingSignaler } from "aurelia-templating-resources";
import { QueriesClientService } from "../../../../../../services/queries/queries-client.services";
import { Claims } from "../../../../../../constants/authentication/claims";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { UnitOfWorkObserver } from "../../../../../../utilities/unit-of-work-observer";
import { RightshipEntityAttachmentModel } from "../../../../../../models/attachment/attachment.models";
import { RSAttachmentClientService } from "../../../../../../services/attachment/rs-attachments-client.service";
import { Reflection } from "../../../../../../utilities/reflection";
import { PscService } from "../../../../../../services/psc/psc.service";

@autoinject
@transient()
@useView('views/pages/modules/psc/steps/documents/psc-documents.html')
export class PscDocuments extends RightshipEntityAttachmentManager {

    public isEditing: boolean;
    public multiple: boolean = true;
    public accept: string = "application/pdf,image/png,image/jpeg,image/jpg,application/zip,application/x-zip-compressed,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.ms-outlook,.msg";
    private _uow: UnitOfWorkObserver<RightshipEntityAttachmentModel[]>;

    constructor(attachmentClientService: AttachmentClientService, notificationService: NotificationClientService, userClaimsService: UserClaimsService,
        dialogService: DialogClientService, i18N: I18N, queriesClientService: QueriesClientService,
        bindingSignaler: BindingSignaler, private bindingEngine: BindingEngine, private rsAttachmentClientService: RSAttachmentClientService,
        private pscService: PscService) {
        super(attachmentClientService, notificationService, userClaimsService, dialogService, i18N, queriesClientService, bindingSignaler);

        if(userClaimsService.hasPermission(Claims.RightShipUser))
        {
            this.showAttachmentTypes = true;
        }
    }

    public openAttachmentsDialog() {
        this.pscService.getPscSetting().then(enableNewUI => {
            if (enableNewUI) {
                this.dialogService.show(RightShipDialogServiceConstants.URLs.PscAttachmentDialog, {
                    rootItem: this.rootItem,
                    entityId: this.entityId,
                    loadAttachments: this.loadAttachments.bind(this)
                }, true, false, false, 'right');
            } else {
                this.dialogService.show(RightShipDialogServiceConstants.URLs.RightshipAttachmentsDialog, this, true, false, false, 'right');
            }
        });
    }

    public openNaviagateAwayConfirmationDialog() {
        return new Promise<boolean>((resolve) => {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.NavigateAwayConfirmationDialog, {
                onStay: () => {
                    resolve(false);
                },
                onLeave: () => {
                    this.isEditing = false;
                    this.selectAll = false;
                    this.stopTracking();
                    resolve(true);
                }
            }, true, false, false, 'center');
        });
    }

    @computedFrom('rootItem', 'entityId')
    public get canAdd(): boolean {
        return super.canAdd && (this.entityId && this.entityId > 0);
    }

    @computedFrom('rootItem', 'entityId')
    public get canDelete(): boolean {
        return super.canDelete && (this.entityId && this.entityId > 0);
    }

    @computedFrom("currentStatus.statusId")
    public get isMisreported(): boolean {
        return this.rootItem &&
            (<PscClientModel>this.rootItem).pscInspectionStatusId == PscInspectionStatusEnum.Misreported;
    }

    public loadAttachments() {
        return new Promise<void>((resolve, reject) => {
            if (this.isLoadingAttachments || !this.shouldLoadAttachments || (this.rootEntityType == null || this.rootItem == null)) {
                resolve();
                return;
            }

            this.isLoadingAttachments = true;

            this.items = [];
            this.stopTracking();
            this.isEditing = false;
            this.selectAll = false;

            this.queriesClientService.getData('odata/RightShipAttachments/Genesis.GetAttachments(entityTypeId='+this.rootEntityType+',entityId=' + (<any>this.rootItem).id + ')').then(results => {
                this.items = results.value;
                this.rootItem["pscDocuments"] = this.items;
                if (this.onAttachmentsLoaded) {
                    this.onAttachmentsLoaded();
                }
                this.isLoadingAttachments = false;
                resolve();
            }).catch(() => {
                this.isLoadingAttachments = false;
                reject();
            });
        });
    }

    toggleEditing() {
        if(!this.isEditing && !this.anyItemsSelected) {
            return
        }
        this.isEditing = !this.isEditing;

        if(this.isEditing)
        {
            this.startTracking();
        }
        else
        {
            this.selectAll = false;
            this.stopTracking();
        }
    }

    attachmentTypesParameters(): string {   
        return `${EntityTypes.PSC },false`
    }

    private startTracking()
    {
        this.stopTracking();
        
        this._uow = new UnitOfWorkObserver<RightshipEntityAttachmentModel[]>(this.bindingEngine, this.items, ["isSelected"], this.handleArrayChanges.bind(this));
        this._uow.startTracking();
    }

    handleArrayChanges = (arrayName, elements, areAdded) => { 
        //if was deleted remove changes from uow
        if(!areAdded)
        {
            for(let i = 0; i < elements.length; i++)
            {
                const id = elements[i]['id'].toString();
                this.deleteChanges(id);
            }
        }
        return [];
    };

    private deleteChanges(id: string, revertChanges: boolean = false)
    {
        if(this.hasChange(id))
        {
            if(revertChanges)
            {
                this.revertChanges(id);
            }
            delete this._uow.unitOfWorks['Array'][id];
        }
    }

    private revertChanges(id: string) {
        if(this.hasChange(id))
        {
            const changes = this._uow.unitOfWorks['Array'][id];
            const properties = Object.keys(changes);
            const attachment = this.items.find(x => x.entityAttachmentId.toString() == id);

            properties.forEach(property => {
                attachment[property] = changes[property].oldValue;
            });
        }
    }

    private hasChange(id: string)
    {
        return this._uow && this._uow.unitOfWorks && Object.keys(this._uow.unitOfWorks).indexOf('Array') > -1
            && Object.keys(this._uow.unitOfWorks['Array']).indexOf(id) > -1;
    }

    private stopTracking()
    {
        if(this._uow)
        {
            this._uow.clearChanges();
            this._uow.dispose();
            this._uow = null;
        }
    }

    async canDeactivate() {
        if (this.isEditing && this._uow && this._uow.hasChanges()) {
            return await this.openNaviagateAwayConfirmationDialog();
        } else {
            this.isEditing = false;
            this.selectAll = false;
            this.stopTracking();
            return true;
        }
    }

    async saveAttachmentChanges()
    {
        if(this._uow && this._uow.hasChanges())
        {
            try {
                //remove any changes of unselected items 
                this.items.filter(x => !x['isSelected'])
                .forEach(item =>
                {
                    this.deleteChanges(item.entityAttachmentId.toString());
                });
                await this.rsAttachmentClientService.updateAttachments({ id: this.entityId, changes: this._uow.getChanges() });
                this.notificationService.show("Successful", "Documents updated successfully.", NotificationType.Success);
                await this.loadAttachments();
            } catch (error) {
                console.error(error);
                this.notificationService.show("Error", "Failed to updated dcouments", NotificationType.Error);
            }
        }
    }

    @computedFrom("_uow", "_uow.getHasChanges")
    get hasAttachmentChanges(): boolean
    {
        return this._uow && this._uow.hasChanges();
    }

    changesDisplayText: string;

    updateChangesText()
    {
        if(this._uow && this._uow.hasChanges())
        {
            const changes = this._uow.getChanges();
            if(changes && Object.keys(changes).indexOf('Array') > -1 && changes['Array'])
            {
                const changedAttachments = changes['Array'];
                const changeCount = Object.keys(changedAttachments).filter(x => x != 'isArray').length;
                this.changesDisplayText = `You have made ${changeCount} ${changeCount == 1? 'change': 'changes'}`;
            }
        }
        else
        {
            this.changesDisplayText = null;
        }
    }

    private _selectAll: boolean;

    set selectAll(value: boolean)
    {
        this._selectAll = value;
        
        if(this.items)
        {
            this.items
            .forEach(item =>
            {
                item['isSelected'] = value;
                if(!this.isEditing)
                {
                    this.revertChanges(item.entityAttachmentId.toString());
                }
            });
        }
        this.onPropertyChanged<PscDocuments>(z => z.items)
    }

    @computedFrom("_selectAll")
    get selectAll(): boolean {
        return this._selectAll;
    }
 
    public itemSelected() { 
        this.onPropertyChanged<PscDocuments>(z => z.items)
    }

    @computedFrom(Reflection.getPropertyPath<PscDocuments>(z => z.items), 
        Reflection.getPropertyPath<PscDocuments>(z => z.changes.items),
        Reflection.getPropertyPath<PscDocuments>(z => z.items.length))
    get anyItemsSelected() {
        return this.items.filter((x) => x['isSelected']).length > 0;
    }
}